import React, { useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Button } from "reactstrap";
import {
	hasChangeHoverTooltip,
	unescapeDisplayMd,
} from "../writingAssistantUtils";
import { writingPreviewMarkdownComponents } from "./writingPreviewMarkdownComponents";
import WritingPreviewChangeStatusBanner from "./WritingPreviewChangeStatusBanner";
import WritingPreviewFixedPopover from "./WritingPreviewFixedPopover";
import WritingPreviewMarkdownWysiwyg from "./WritingPreviewMarkdownWysiwyg";
import { useChangeReasoningPopover } from "./useChangeReasoningPopover";

/**
 * Single AI change card with optional inline editing.
 *
 * Editing props (all optional) are supplied by WritingPreviewLayout when
 * useWritingPreviewEdits has been wired up. When omitted the component
 * behaves exactly like it did before (read-only).
 */
export default function WritingPreviewChangeCallout({
	change,
	changeIndex,
	isActive,
	innerRef,
	latestValidation,

	// Edit plumbing (optional)
	editable = false,
	isSavingAll = false,
	isSavingThis = false,
	draftValue = null,
	isDirty = false,
	isSaved = false,
	rejectionMessage = null,
	onEditToggle = null,
	onDraftChange = null,
	onClearDraft = null,
	// Single-block save. If omitted, user must use the toolbar "Save all" button.
	onSaveSingle = null,
}) {
	// Strict-normalize flags. NaN / undefined / "false" strings must never accidentally
	// pass truthy checks. (Fixes spurious "Saving…" showing on a fresh page load.)
	const _editable = Boolean(editable);
	const _isSavingAll = Boolean(isSavingAll);
	const _isSavingThis = Boolean(isSavingThis);
	const _isDirty = Boolean(isDirty);
	const _isSaved = Boolean(isSaved);
	// Show save button ONLY when: block was actually edited (isDirty=true with a draft value)
	// OR a save is genuinely in-flight for THIS block (starts a save, then the draft
	// might already be cleared but spinner still shows for a frame).
	const _showSaveButton =
		_editable && typeof onSaveSingle === "function" && (_isDirty || _isSavingThis);
	const _anySaving = _isSavingAll || _isSavingThis;
	const typeClass =
		change.type === "added"
			? "writing-preview__callout--added"
			: "writing-preview__callout--updated";

	const showHoverPopover = hasChangeHoverTooltip(change);
	const { popoverProps, getAnchorHandlers } = useChangeReasoningPopover();
	const hoverHandlers = showHoverPopover
		? getAnchorHandlers(change, { includeWhy: true })
		: {};

	const [isEditing, setIsEditing] = useState(false);

	const baseMd = useMemo(
		() =>
			unescapeDisplayMd(
				change.newContentMd ?? null,
				change.newContentText || ""
			),
		[change.newContentMd, change.newContentText]
	);

	// User drafts (including intentional empty string) must never fall back to AI copy.
	const currentMd = useMemo(() => {
		if (typeof draftValue === "string") {
			return unescapeDisplayMd(draftValue, "");
		}
		return baseMd;
	}, [draftValue, baseMd]);

	const classes = [
		"writing-preview__callout",
		typeClass,
		isActive ? "writing-preview__callout--active" : "",
		_isDirty ? "writing-preview__callout--dirty" : "",
		_isSaved ? "writing-preview__callout--saved" : "",
		isEditing ? "writing-preview__callout--editing" : "",
		rejectionMessage ? "writing-preview__callout--rejected" : "",
	]
		.filter(Boolean)
		.join(" ");

	const handleToggleEdit = useCallback(() => {
		if (!_editable) return;
		const next = !isEditing;
		setIsEditing(next);
		if (typeof onEditToggle === "function") {
			onEditToggle(changeIndex, next);
		}
	}, [_editable, isEditing, onEditToggle, changeIndex]);

	const handleDraftChange = useCallback(
		(md) => {
			if (!_editable) return;
			if (typeof onDraftChange === "function") {
				onDraftChange(changeIndex, typeof md === "string" ? md : "", baseMd);
			}
		},
		[_editable, onDraftChange, changeIndex, baseMd]
	);

	const handleCancelEdit = useCallback(() => {
		if (!_editable) return;
		setIsEditing(false);
		if (typeof onClearDraft === "function") {
			onClearDraft(changeIndex);
		}
	}, [_editable, onClearDraft, changeIndex]);

	const handleSaveSingle = useCallback(() => {
		if (!_editable) return;
		if (typeof onSaveSingle === "function") {
			onSaveSingle(changeIndex);
		}
	}, [_editable, onSaveSingle, changeIndex]);

	return (
		<>
			<div
				ref={innerRef}
				data-change-index={changeIndex}
				className={classes}
			>
				<div className="writing-preview__callout-header">
					<div className="writing-preview__callout-header-left">
						<span
							className={`writing-preview__change-badge writing-preview__change-badge--${change.type}`}
						>
							<i className="fas fa-magic me-1" />
							{change.type === "added" ? "ADDED BY AI" : "UPDATED BY AI"}
						</span>
						&nbsp;
						<span className="writing-preview__change-tag">&lt;{change.tag}&gt;</span>
						{_editable && rejectionMessage && (
							<span className="writing-preview__change-pill writing-preview__change-pill--rejected">
								<i className="fas fa-exclamation-triangle me-1" />
								Needs review
							</span>
						)}
					</div>
					<div className="writing-preview__callout-header-right">
						{_editable && (
							<>
								{/* Save button – visible when this block has a dirty draft, or a save
									   is in-flight specifically for this block. Strict _showSaveButton
									   prevents spurious "Saving…" on a fresh page load. */}
								{_showSaveButton && (
									<Button
										type="button"
										color="primary"
										size="sm"
										className="writing-preview__callout-action writing-preview__callout-save-btn me-2"
										disabled={!_isDirty || _anySaving}
										onClick={_isDirty && !_anySaving ? handleSaveSingle : undefined}
									>
										{_isSavingThis ? (
											<>
												<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
												Saving…
											</>
										) : (
											<>
												<i className="fas fa-save me-1" />
												Save
											</>
										)}
									</Button>
								)}
								{isEditing && _isDirty && (
									<Button
										type="button"
										color="light"
										size="sm"
										className="writing-preview__callout-action border me-2"
										disabled={_anySaving}
										onClick={_anySaving ? undefined : handleCancelEdit}
									>
										<i className="fas fa-undo me-1" />
										Discard
									</Button>
								)}
								<Button
									type="button"
									color="light"
									size="sm"
									className="writing-preview__callout-action border"
									disabled={_anySaving}
									onClick={_anySaving ? undefined : handleToggleEdit}
								>
									{isEditing ? (
										<>
											<i className="fas fa-eye me-1" />
											Preview
										</>
									) : (
										<>
											<i className="fas fa-pen me-1" />
											Edit
										</>
									)}
								</Button>
							</>
						)}
						<WritingPreviewChangeStatusBanner
							latestValidation={latestValidation}
							changeIndex={changeIndex}
						/>
					</div>
				</div>
				<div
					className={`writing-preview__callout-body ${
						showHoverPopover && !isEditing
							? "writing-preview__callout--has-tooltip"
						: ""
					}`}
					{...(!isEditing ? hoverHandlers : {})}
				>
					{change.reasoning?.trim() ? (
						<p className="writing-preview__callout-reasoning">{change.reasoning}</p>
					) : null}
					{_editable && rejectionMessage ? (
						<div className="writing-preview__callout-rejection">
							<i className="fas fa-exclamation-circle me-2" />
							{rejectionMessage}
						</div>
					) : null}
					<hr className="writing-preview__callout-divider" />
					{_editable && isEditing ? (
						<div className="writing-preview__callout-editor">
							<WritingPreviewMarkdownWysiwyg
								value={currentMd}
								onChange={handleDraftChange}
								disabled={_anySaving}
							/>
							<p className="writing-preview__callout-editor-hint text-muted small mb-0 mt-2">
								Use the toolbar to format; content is saved as markdown for the pipeline.
							</p>
						</div>
					) : (
						<div className="writing-preview__callout-content">
							<ReactMarkdown components={writingPreviewMarkdownComponents}>
								{currentMd || ""}
							</ReactMarkdown>
						</div>
					)}
				</div>
			</div>
			<WritingPreviewFixedPopover {...popoverProps} />
		</>
	);
}
